import { useEffect, useMemo, useState } from 'react';
import { LuChevronRight, LuDownload } from 'react-icons/lu';
import {
    Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import PageHeader from '../components/PageHeader';
import MonthPicker from '../components/MonthPicker';
import { ListRow } from '../components/ListKit';
import { useChartTokens, useTooltipStyle } from '../components/ChartKit';
import {
    VisaoGeralSistemaService,
    type SituacaoEmpresa,
    type VisaoGeralEmpresa,
    type VisaoGeralResposta,
    type VisaoGeralUsuario,
} from '../services/VisaoGeralSistemaService';
import './VisaoGeralSistemaPage.css';

/**
 * Visão geral — área Sistema (só GB Code), primeiro item de Relatórios.
 *
 * A plataforma em números para a diretoria, numa tela só: a BASE (empresas, assinaturas,
 * usuários, receita de hoje), o USO (quem entrou, logins, conexões), quatro gráficos e a
 * lista de empresas por uso. A lista é a parte que gera ação: quem está em teste e não
 * entra é lead frio; quem paga e não entra é cancelamento a caminho.
 *
 * "Usuário ativo" é quem entrou no período (decisão de 05/10/2026), com cadastrados como
 * denominador. Proposta discutida em chat em 05/10/2026, sem canvas.
 */

type Modo = 'mes' | '30d';

const CHAVE_MODO = 'sis-visao-geral-modo';
const CHAVE_MES = 'sis-visao-geral-mes';

const COLUNAS = 'minmax(240px, 1fr) 140px 100px 160px 150px 150px';

const SITUACOES: Record<SituacaoEmpresa, { rotulo: string; classe: string }> = {
    vigente: { rotulo: 'Vigente', classe: 'is-ok' },
    teste: { rotulo: 'Teste', classe: 'is-teste' },
    sem_assinatura: { rotulo: 'Sem assinatura', classe: 'is-sem' },
};

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const mesCorrente = () => iso(new Date()).slice(0, 7);

const limitesDoMes = (mes: string) => {
    const [ano, m] = mes.split('-').map(Number);
    const ultimo = new Date(ano, m, 0).getDate();
    return { de: `${mes}-01`, ate: `${mes}-${String(ultimo).padStart(2, '0')}` };
};

const ultimos30Dias = () => {
    const hoje = new Date();
    const inicio = new Date(hoje);
    inicio.setDate(hoje.getDate() - 29);
    return { de: iso(inicio), ate: iso(hoje) };
};

const numero = (v: number) => v.toLocaleString('pt-BR');
const plural = (v: number, um: string, varios: string) => `${numero(v)} ${v === 1 ? um : varios}`;
const dinheiro = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataCurta = (valor: string | null) =>
    valor ? new Date(valor).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

/** `2026-10-10T17:32:00Z` → `10/10/2026 14:32`, no fuso de quem olha. */
const dataHora = (valor: string) => {
    const d = new Date(valor);
    return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

/** `2026-09-06` → `06/09`; `2026-09` → `set`. */
const diaCurto = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesCurto = (mes: string) => MESES[Number(mes.slice(5, 7)) - 1];

const VisaoGeralSistemaPage = () => {
    const [modo, setModo] = useState<Modo>(() => (sessionStorage.getItem(CHAVE_MODO) as Modo) || 'mes');
    const [mes, setMes] = useState(() => sessionStorage.getItem(CHAVE_MES) || mesCorrente());

    const [dados, setDados] = useState<VisaoGeralResposta | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => { sessionStorage.setItem(CHAVE_MODO, modo); }, [modo]);
    useEffect(() => { sessionStorage.setItem(CHAVE_MES, mes); }, [mes]);

    const { de, ate } = useMemo(() => (modo === 'mes' ? limitesDoMes(mes) : ultimos30Dias()), [modo, mes]);

    useEffect(() => {
        let cancelado = false;

        setCarregando(true);
        setErro(null);

        VisaoGeralSistemaService.buscar(de, ate)
            .then(d => { if (!cancelado) setDados(d); })
            .catch(e => { if (!cancelado) setErro(e instanceof Error ? e.message : 'Não foi possível carregar.'); })
            .finally(() => { if (!cancelado) setCarregando(false); });

        return () => { cancelado = true; };
    }, [de, ate]);

    const periodoRotulo = modo === 'mes' ? 'no mês' : 'nos últimos 30 dias';

    const exportar = () => {
        if (!dados) return;
        const linhas = [
            ['Empresa', 'Situação', 'Usuários', 'Entraram no período', 'Último login', 'Último acesso'],
            ...dados.empresas.map(e => [
                e.nome, SITUACOES[e.situacao]?.rotulo ?? e.situacao, String(e.usuarios),
                String(e.entraramNoPeriodo), e.ultimaSessao ? dataHora(e.ultimaSessao) : '',
                e.ultimoAcesso ? dataHora(e.ultimoAcesso) : '',
            ]),
        ];
        // Ponto e vírgula porque o Excel em pt-BR usa vírgula como decimal.
        const csv = linhas.map(l => l.map(c => `"${c.replace(/"/g, '""')}"`).join(';')).join('\n');
        const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `visao-geral-${dados.de}-${dados.ate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="page-shell lk-screen sis-vg">
            <PageHeader
                title="Visão geral"
                subtitle="A plataforma em números: base, uso e receita de todas as empresas"
                actions={
                    <div className="sis-vg-acoes">
                        <div className="sis-vg-seg" role="group" aria-label="Período">
                            <button type="button" className={modo === 'mes' ? 'is-on' : ''} onClick={() => setModo('mes')}>Mês</button>
                            <button type="button" className={modo === '30d' ? 'is-on' : ''} onClick={() => setModo('30d')}>Últimos 30 dias</button>
                        </div>
                        {modo === 'mes' && <MonthPicker value={mes} onChange={setMes} />}
                        <button type="button" className="sis-vg-btn" onClick={exportar} disabled={!dados || carregando}>
                            <LuDownload size={15} />
                            <span>Exportar</span>
                        </button>
                    </div>
                }
            />

            <div className="page-body">
                {erro && <div className="sis-vg-aviso is-erro">{erro}</div>}

                <Base dados={dados} carregando={carregando} periodoRotulo={periodoRotulo} />
                <Uso dados={dados} carregando={carregando} periodoRotulo={periodoRotulo} />
                <Graficos dados={dados} />
                <Empresas dados={dados} carregando={carregando} periodoRotulo={periodoRotulo} de={de} ate={ate} />
            </div>
        </div>
    );
};

/** Bloco de número do topo — o mesmo molde de Mensagens e Assistente. */
const Bloco = ({ rotulo, valor, apoio, alerta, denominador }: {
    rotulo: string; valor: string; apoio?: string; alerta?: boolean; denominador?: string;
}) => (
    <div className={`sis-vg-tile ${alerta ? 'is-alerta' : ''}`}>
        <div className="sis-vg-tile-k">{rotulo}</div>
        <div className="sis-vg-tile-v">
            {valor}
            {denominador && <small>{denominador}</small>}
        </div>
        {apoio && <div className="sis-vg-tile-d">{apoio}</div>}
    </div>
);

const Base = ({ dados, carregando, periodoRotulo }: { dados: VisaoGeralResposta | null; carregando: boolean; periodoRotulo: string }) => {
    const b = dados?.base;
    const traco = carregando || !b;
    return (
        <>
            <h2 className="sis-vg-sec">Base</h2>
            <div className="sis-vg-tiles">
                <Bloco
                    rotulo="Empresas"
                    valor={traco ? '—' : numero(b.empresas)}
                    apoio={b ? `${numero(b.empresasEmTeste)} em teste · ${numero(b.empresasComAssinaturaVigente)} com assinatura vigente · ${numero(b.empresasSemAssinatura)} sem assinatura` : undefined}
                />
                <Bloco
                    rotulo={`Novas empresas ${periodoRotulo}`}
                    valor={traco ? '—' : numero(b.empresasNovasNoPeriodo)}
                    apoio={b ? `${plural(b.usuariosNovosNoPeriodo, 'usuário novo', 'usuários novos')} no mesmo período` : undefined}
                />
                <Bloco
                    rotulo="Usuários cadastrados"
                    valor={traco ? '—' : numero(b.usuarios)}
                    apoio={b ? `${numero(b.usuariosComGoogle)} com conta Google vinculada` : undefined}
                />
                <Bloco
                    rotulo="Receita mensal hoje"
                    valor={traco ? '—' : dinheiro(b.mrrHoje)}
                    apoio={b ? `${plural(b.assinaturasPagas, 'paga', 'pagas')} · ${plural(b.assinaturasPendentes, 'pendente', 'pendentes')} · ${plural(b.assinaturasCanceladas, 'cancelada', 'canceladas')} · foto de hoje, não série` : undefined}
                />
            </div>
        </>
    );
};

const Uso = ({ dados, carregando, periodoRotulo }: { dados: VisaoGeralResposta | null; carregando: boolean; periodoRotulo: string }) => {
    const u = dados?.uso;
    const b = dados?.base;
    const traco = carregando || !u || !b;

    const apoioLogins = !u ? undefined
        : u.loginsAproximados
            ? (u.medindoLoginsDesde
                ? `aproximado pelo token: o evento de login só existe desde ${dataCurta(u.medindoLoginsDesde)}`
                : 'aproximado pelo token: o evento de login ainda não foi gravado')
            : `${numero(u.loginsGoogle ?? 0)} pelo Google`;

    return (
        <>
            <h2 className="sis-vg-sec">Uso {periodoRotulo}</h2>
            <div className="sis-vg-tiles">
                <Bloco
                    rotulo="Empresas que entraram"
                    valor={traco ? '—' : numero(u.empresasQueEntraram)}
                    denominador={b ? `de ${numero(b.empresas)}` : undefined}
                    apoio="com pelo menos uma pessoa que entrou"
                />
                <Bloco
                    rotulo="Usuários que entraram"
                    valor={traco ? '—' : numero(u.usuariosQueEntraram)}
                    denominador={b ? `de ${numero(b.usuarios)}` : undefined}
                    apoio="o número de usuários ativos da diretoria"
                />
                <Bloco
                    rotulo="Logins"
                    valor={traco ? '—' : numero(u.logins)}
                    apoio={apoioLogins}
                    alerta={u?.loginsAproximados}
                />
                <Bloco
                    rotulo="Conexões ativas"
                    valor={traco ? '—' : numero(u.conexoesAtivas)}
                    denominador={u ? `de ${numero(u.conexoesTotal)}` : undefined}
                    apoio={u ? `${numero(u.conversasNoPeriodo)} conversas · ${numero(u.perguntasAssistenteNoPeriodo)} perguntas à IA · bot ligado em ${numero(u.empresasComBotLigado)} empresas` : undefined}
                />
            </div>
        </>
    );
};

const Graficos = ({ dados }: { dados: VisaoGeralResposta | null }) => {
    const tokens = useChartTokens();
    const tooltip = useTooltipStyle();

    const porDia = useMemo(() => (dados?.series.entradasPorDia ?? []).map(p => ({ ...p, rotulo: diaCurto(p.dia) })), [dados]);
    const empresasMes = useMemo(() => (dados?.series.empresasNovasPorMes ?? []).map(p => ({ ...p, rotulo: mesCurto(p.mes) })), [dados]);
    const conversasMes = useMemo(() => (dados?.series.conversasPorMes ?? []).map(p => ({ ...p, rotulo: mesCurto(p.mes) })), [dados]);

    const b = dados?.base;
    const situacoes = b ? [
        { rotulo: 'Em teste', valor: b.empresasEmTeste },
        { rotulo: 'Assinatura vigente', valor: b.empresasComAssinaturaVigente },
        { rotulo: 'Sem assinatura', valor: b.empresasSemAssinatura },
    ] : [];
    const maiorSituacao = Math.max(1, ...situacoes.map(s => s.valor));

    const eixo = { axisLine: false, tickLine: false, tick: { fill: tokens.axis, fontSize: 11 } };

    return (
        <div className="sis-vg-graficos">
            <div className="sis-vg-card is-largo">
                <div className="sis-vg-card-t">Usuários que entraram, por dia</div>
                <div className="sis-vg-card-s">pessoas distintas com entrada no dia</div>
                <ResponsiveContainer width="100%" height={170}>
                    <BarChart data={porDia} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={tokens.grid} vertical={false} />
                        <XAxis dataKey="rotulo" {...eixo} interval="preserveStartEnd" minTickGap={28} />
                        <YAxis {...eixo} width={30} allowDecimals={false} />
                        <Tooltip
                            {...tooltip}
                            cursor={{ fill: tokens.grid }}
                            formatter={(v: number, nome: string) => [numero(v), nome === 'usuarios' ? 'Usuários' : 'Logins']}
                            labelFormatter={(r: string) => `Dia ${r}`}
                        />
                        <Bar dataKey="usuarios" fill={tokens.primary} radius={[3, 3, 0, 0]} maxBarSize={22} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="sis-vg-card">
                <div className="sis-vg-card-t">Empresas por situação</div>
                <div className="sis-vg-card-s">hoje</div>
                <div className="sis-vg-situacoes">
                    {situacoes.map(s => (
                        <div className="sis-vg-sit" key={s.rotulo}>
                            <span className="sis-vg-sit-r">{s.rotulo}</span>
                            <span className="sis-vg-sit-b"><i style={{ width: `${(s.valor / maiorSituacao) * 100}%` }} /></span>
                            <span className="sis-vg-sit-v">{numero(s.valor)}</span>
                        </div>
                    ))}
                </div>
                <div className="sis-vg-card-n">Vigente = vencimento no futuro. Sem assinatura = o teste acabou e não contratou.</div>
            </div>

            <div className="sis-vg-card">
                <div className="sis-vg-card-t">Novas empresas por mês</div>
                <div className="sis-vg-card-s">últimos 12 meses</div>
                <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={empresasMes} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={tokens.grid} vertical={false} />
                        <XAxis dataKey="rotulo" {...eixo} />
                        <YAxis {...eixo} width={30} allowDecimals={false} />
                        <Tooltip {...tooltip} cursor={{ fill: tokens.grid }} formatter={(v: number) => [numero(v), 'Novas empresas']} />
                        <Bar dataKey="valor" fill={tokens.primary} radius={[3, 3, 0, 0]} maxBarSize={28} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="sis-vg-card">
                <div className="sis-vg-card-t">Conversas abertas por mês</div>
                <div className="sis-vg-card-s">últimos 12 meses · operação dos clientes</div>
                <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={conversasMes} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={tokens.grid} vertical={false} />
                        <XAxis dataKey="rotulo" {...eixo} />
                        <YAxis {...eixo} width={44} allowDecimals={false} tickFormatter={(v: number) => numero(v)} />
                        <Tooltip {...tooltip} formatter={(v: number) => [numero(v), 'Conversas']} />
                        <Line type="monotone" dataKey="valor" stroke={tokens.primary} strokeWidth={2} dot={{ r: 3, fill: tokens.primary, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

/**
 * A lista de empresas. Abrir a linha mostra quem é quem lá dentro, cada usuário com a sua
 * última sessão (pedido de 10/10/2026): a coluna da empresa só diz que ALGUÉM entrou. Os
 * usuários vêm sob demanda, um pedido por empresa aberta, e fecham quando o período muda.
 *
 * "Último login" é quando entrou; "Último acesso" (17/10/2026) é quando usou o CRM pela última vez.
 * A sessão dura 7 dias, então quem entra de manhã e trabalha o dia todo segue com o login das 8h:
 * é o acesso que diz se a pessoa está usando. Modo suporte e aba em segundo plano não contam.
 */
const Empresas = ({ dados, carregando, periodoRotulo, de, ate }: {
    dados: VisaoGeralResposta | null; carregando: boolean; periodoRotulo: string; de: string; ate: string;
}) => {
    const [abertas, setAbertas] = useState<Set<number>>(() => new Set());

    useEffect(() => { setAbertas(new Set()); }, [de, ate]);

    const alternar = (empresaId: number) => setAbertas(atual => {
        const proximo = new Set(atual);
        if (proximo.has(empresaId)) proximo.delete(empresaId); else proximo.add(empresaId);
        return proximo;
    });

    return (
    <div className="sis-vg-lista">
        <div className="sis-vg-lista-t">Empresas</div>
        <div className="sis-vg-lista-s">quem está usando e quem parou · ordenadas por uso {periodoRotulo}</div>

        <div className="lk-table">
            <div className="lk-scroll">
                <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                    <div className="lk-head">Empresa</div>
                    <div className="lk-head">Situação</div>
                    <div className="lk-head sis-vg-r">Usuários</div>
                    <div className="lk-head sis-vg-r">Entraram {periodoRotulo}</div>
                    <div className="lk-head sis-vg-r">Último login</div>
                    <div className="lk-head sis-vg-r">Último acesso</div>
                </div>
                <div className="lk-body">
                    {carregando
                        ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                        : (dados?.empresas ?? []).map(e => (
                            <LinhaEmpresa
                                key={e.empresaId}
                                empresa={e}
                                aberta={abertas.has(e.empresaId)}
                                onAlternar={() => alternar(e.empresaId)}
                                de={de}
                                ate={ate}
                            />
                        ))}

                    {!carregando && (dados?.empresas.length ?? 0) === 0 && (
                        <div className="sis-vg-vazio">Nenhuma empresa cadastrada.</div>
                    )}
                </div>
            </div>
        </div>
    </div>
    );
};

const LinhaEmpresa = ({ empresa: e, aberta, onAlternar, de, ate }: {
    empresa: VisaoGeralEmpresa; aberta: boolean; onAlternar: () => void; de: string; ate: string;
}) => {
    const sit = SITUACOES[e.situacao] ?? { rotulo: e.situacao, classe: 'is-sem' };
    const abre = e.usuarios > 0;

    return (
        <>
            <ListRow
                columns={COLUNAS}
                onOpen={abre ? onAlternar : undefined}
                label={abre ? `${aberta ? 'Fechar' : 'Ver'} os usuários de ${e.nome}` : undefined}
            >
                <span className="lk-cell">
                    <span className={`sis-vg-seta ${aberta ? 'is-aberta' : ''} ${abre ? '' : 'is-vazia'}`} aria-hidden="true">
                        <LuChevronRight size={14} />
                    </span>
                    <span className="sis-vg-nome">{e.nome}</span>
                </span>
                <span className="lk-cell"><span className={`sis-vg-pill ${sit.classe}`}><i />{sit.rotulo}</span></span>
                <span className="lk-cell sis-vg-r">{e.usuarios ? numero(e.usuarios) : <i className="sis-vg-zero">0</i>}</span>
                <span className="lk-cell sis-vg-r">{e.entraramNoPeriodo ? numero(e.entraramNoPeriodo) : <i className="sis-vg-zero">0</i>}</span>
                <span className="lk-cell sis-vg-r">{e.ultimaSessao ? dataHora(e.ultimaSessao) : <i className="sis-vg-zero">nunca</i>}</span>
                <span className="lk-cell sis-vg-r">{e.ultimoAcesso ? dataHora(e.ultimoAcesso) : <i className="sis-vg-zero">nunca</i>}</span>
            </ListRow>
            {aberta && <UsuariosDaEmpresa empresaId={e.empresaId} de={de} ate={ate} />}
        </>
    );
};

const UsuariosDaEmpresa = ({ empresaId, de, ate }: { empresaId: number; de: string; ate: string }) => {
    const [usuarios, setUsuarios] = useState<VisaoGeralUsuario[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        let cancelado = false;
        setUsuarios(null);
        setErro(null);
        VisaoGeralSistemaService.usuariosDaEmpresa(empresaId, de, ate)
            .then(u => { if (!cancelado) setUsuarios(u); })
            .catch(err => { if (!cancelado) setErro(err instanceof Error ? err.message : 'Não foi possível carregar os usuários.'); });
        return () => { cancelado = true; };
    }, [empresaId, de, ate]);

    if (erro) return <div className="sis-vg-sub-aviso">{erro}</div>;
    if (!usuarios) return <div className="lk-skeleton sis-vg-sub-skeleton" />;
    if (usuarios.length === 0) return <div className="sis-vg-sub-aviso">Nenhum usuário ativo nesta empresa.</div>;

    return (
        <>
            {usuarios.map(u => (
                <div key={u.usuarioId} className="lk-row sis-vg-sub" style={{ gridTemplateColumns: COLUNAS }}>
                    <span className="lk-cell">
                        <span className="sis-vg-sub-nome">{u.nome}</span>
                        <span className="sis-vg-sub-email">{u.email}</span>
                    </span>
                    <span className="lk-cell sis-vg-sub-perfil">{u.perfil ?? <i className="sis-vg-zero">sem perfil</i>}</span>
                    <span className="lk-cell" />
                    <span className="lk-cell sis-vg-r">
                        {u.entradasNoPeriodo ? plural(u.entradasNoPeriodo, 'entrada', 'entradas') : <i className="sis-vg-zero">0</i>}
                    </span>
                    <span className="lk-cell sis-vg-r">{u.ultimaSessao ? dataHora(u.ultimaSessao) : <i className="sis-vg-zero">nunca</i>}</span>
                    <span className="lk-cell sis-vg-r">{u.ultimoAcesso ? dataHora(u.ultimoAcesso) : <i className="sis-vg-zero">nunca</i>}</span>
                </div>
            ))}
        </>
    );
};

export default VisaoGeralSistemaPage;
